/**
 * WordPress dependencies
 */
import {
	RangeControl,
	ToolsPanel,
	ToolsPanelItem,
	useCustomUnits,
	type BoxControlValue,
} from '@safe-wordpress/components';
import { _x } from '@safe-wordpress/i18n';

/**
 * Internal dependencies
 */
import type { ToolsPanelProps } from './types';
import {
	PositionControl,
	getEditorSettings,
	type FullEditorSettings,
} from './internal';
import {
	getPreviewValue,
	hasInheritedValue,
	hasUserValue,
} from '../../../../utils/props';

export function useHasPositioningPanel(
	settings: FullEditorSettings
): boolean {
	const hasPosition = useHasPositionControl( settings );
	const hasZIndex = useHasZIndexControl( settings );

	return hasPosition || hasZIndex;
}

function useHasPositionControl( settings?: FullEditorSettings ): boolean {
	return !! settings?.position;
}

function useHasZIndexControl( settings?: FullEditorSettings ): boolean {
	return !! settings?.position?.zIndex;
}

export function PositioningPanel( {
	value: props,
	onChange,
}: ToolsPanelProps ): JSX.Element {
	const settings = getEditorSettings();
	const units = useCustomUnits( {
		availableUnits: [ '%', 'px', 'em', 'rem', 'vw' ],
	} );

	// Position
	const hasPositionEnabled = useHasPositionControl( settings );
	const disabledSides = useDisabledSides( props );
	const sides = settings.position.sides;

	// Z Index
	const hasZIndexEnabled = useHasZIndexControl( settings );
	const zIndexValue = isNaN(
		parseInt( getPreviewValue( props.zIndex ) || '0' )
	)
		? 0
		: parseInt( getPreviewValue( props.zIndex ) || '0' );

	const resetAll = () =>
		onChange( {
			position: undefined,
			zIndex: undefined,
			top: undefined,
			right: undefined,
			bottom: undefined,
			left: undefined,
		} );

	return (
		<ToolsPanel
			label={ _x( 'Positioning', 'text', 'nelio-ab-testing' ) }
			resetAll={ resetAll }
		>
			{ hasPositionEnabled && (
				<ToolsPanelItem
					className="single-column"
					label={ _x( 'Position', 'text', 'nelio-ab-testing' ) }
					hasValue={ () => hasUserValue( props.position ) }
					onSelect={ () => onChange( { position: 'static' } ) }
					onDeselect={ () =>
						onChange( {
							position: undefined,
							top: undefined,
							right: undefined,
							bottom: undefined,
							left: undefined,
						} )
					}
					isShownByDefault={ hasInheritedValue( props.position ) }
				>
					<PositionControl
						values={ {
							position: getPreviewValue( props.position ),
							top: getPreviewValue( props.top ),
							right: getPreviewValue( props.right ),
							bottom: getPreviewValue( props.bottom ),
							left: getPreviewValue( props.left ),
						} }
						onChange={ onChange }
						units={ units }
						sides={ sides as ( keyof BoxControlValue )[] }
						size="__unstable-large"
						disabledPosition={ 'readonly' === props.position?.type }
						disabledSides={ disabledSides }
					/>
				</ToolsPanelItem>
			) }
			{ hasZIndexEnabled && (
				<ToolsPanelItem
					label={ _x( 'Z Index', 'text', 'nelio-ab-testing' ) }
					hasValue={ () => hasUserValue( props.zIndex ) }
					onSelect={ () => onChange( { zIndex: 'auto' } ) }
					onDeselect={ () => onChange( { zIndex: undefined } ) }
					isShownByDefault={ hasInheritedValue( props.zIndex ) }
				>
					<RangeControl
						label={ _x( 'Z Index', 'text', 'nelio-ab-testing' ) }
						value={ zIndexValue }
						initialPosition={ 0 }
						currentInput={ 0 }
						onChange={ ( next ) =>
							onChange( {
								zIndex:
									next !== undefined
										? `${ next }`
										: undefined,
							} )
						}
						step={ 1 }
						min={ -10 }
						max={ Math.max( 100, zIndexValue + 100 ) }
						disabled={ 'readonly' === props.zIndex?.type }
					/>
				</ToolsPanelItem>
			) }
		</ToolsPanel>
	);
}

// =======
// HELPERS
// =======

function useDisabledSides( value: ToolsPanelProps[ 'value' ] ) {
	return {
		top: 'readonly' === value.top?.type,
		right: 'readonly' === value.right?.type,
		bottom: 'readonly' === value.bottom?.type,
		left: 'readonly' === value.left?.type,
	};
}
